import React, { useRef, useEffect } from 'react';
import { useTheme } from '@mui/material';
import { useKeyboard } from '../hooks/useKeyboard';

const NativeInput = ({ 
  type = 'text',
  placeholder = '',
  value = '',
  onChange = () => {},
  onFocus,
  maxLength,
  autoFocus = false,
  disabled = false,
  id,
  'data-testid': dataTestId,
  style = {},
  ...props 
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const { showKeyboard } = useKeyboard();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Small delay to ensure the component is mounted
      setTimeout(() => {
        inputRef.current?.focus();
        if (window.Capacitor?.isNativePlatform()) {
          showKeyboard();
        }
      }, 100);
    }
  }, [autoFocus, showKeyboard]);

  const handleFocus = async (e) => {
    // Ensure the input is focused
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Call the passed onFocus handler
    if (onFocus) {
      onFocus(e);
    }
    
    if (window.Capacitor?.isNativePlatform()) {
      try {
        await showKeyboard();
      } catch (error) {
        console.log('Error showing keyboard on focus:', error);
      }
    }
  };

  const handleClick = async (e) => {
    // Handle click to ensure focus and keyboard show
    e.preventDefault();
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    if (window.Capacitor?.isNativePlatform()) {
      try {
        await showKeyboard();
      } catch (error) {
        console.log('Error showing keyboard on click:', error);
      }
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '16px',
    fontSize: '16px', // Prevents zoom on iOS
    border: `2px solid ${theme.palette.grey[300]}`,
    borderRadius: '12px',
    backgroundColor: '#fff',
    outline: 'none',
    transition: 'border-color 0.3s ease',
    fontFamily: 'inherit',
    ...style,
  };

  const focusStyle = {
    borderColor: theme.palette.primary.main,
  };

  return (
    <input
      ref={inputRef}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={handleFocus}
      onClick={handleClick}
      maxLength={maxLength}
      disabled={disabled}
      id={id}
      data-testid={dataTestId}
      style={inputStyle}
      {...props}
    />
  );
};

export default NativeInput;
